// Attachment Service for Questions Module
class AttachmentService {
  constructor() {
    this.API_BASE_URL = '/api/questions';
  }

  // Upload attachment to a question
  async uploadAttachment(questionId, file, uploaderId, attachmentType = 'file') {
    try {
      console.log(`Uploading file: ${file.name} to question ${questionId}`);
      
      // Validate file first
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }
      
      // Convert file to base64
      const fileBase64 = await this._fileToBase64(file);
      
      // Prepare payload for new backend endpoint
      const payload = {
        file_data: fileBase64.split(',')[1], // Remove data:type/subtype;base64, prefix
        metadata: {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          uploaded_by: uploaderId,
          attachment_type: attachmentType
        }
      };
      
      console.log(`Sending upload request to: ${this.API_BASE_URL}/${questionId}/upload`);
      
      const response = await fetch(`${this.API_BASE_URL}/${questionId}/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      
      return { 
        success: true, 
        data: result.data || result 
      };
      
    } catch (error) {
      console.error('Upload error:', error);
      console.warn('API not available or upload failed, using mock upload:', error.message);
      
      // Mock upload for demo/fallback
      const mockAttachment = {
        id: Date.now(),
        question_id: questionId,
        filename: file.name,
        file_type: this.getFileType(file.name),
        file_size: file.size,
        file_url: URL.createObjectURL(file),
        attachment_type: attachmentType,
        uploaded_by: uploaderId,
        uploader_name: 'Current User',
        created_at: new Date().toISOString(),
        question_title: `Question ${questionId}`
      };

      return { success: true, data: mockAttachment };
    }
  }

  // Helper: Convert File to base64 string
  _fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // Get attachments for a specific question
  async getQuestionAttachments(questionId) {
    try {
      console.log(`Fetching attachments for question ${questionId}`);
      const response = await fetch(`${this.API_BASE_URL}/${questionId}/attachments`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`Attachments response:`, result);
      return { success: true, data: result.data || result.attachments || [] };
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      
      // Mock data for demo
      const mockAttachments = [
        {
          id: 1,
          question_id: questionId,
          filename: 'document.pdf',
          file_type: 'pdf',
          file_size: 1024000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user1',
          uploader_name: 'John Doe',
          created_at: '2024-01-15T10:30:00Z',
          question_title: 'Sample Question'
        },
        {
          id: 2,
          question_id: questionId,
          filename: 'screenshot.png',
          file_type: 'image',
          file_size: 512000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user2',
          uploader_name: 'Jane Smith',
          created_at: '2024-01-14T14:20:00Z',
          question_title: 'Sample Question'
        }
      ];

      return { success: true, data: mockAttachments };
    }
  }

  // Get all attachments (for AttachmentsPage)
  async getAllAttachments() {
    try {
      const response = await fetch(`${this.API_BASE_URL}/attachments`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('getAllAttachments response:', result);
      return { success: true, data: result.data || result.attachments || [] };
    } catch (error) {
      console.warn('API not available, using mock data:', error.message);
      
      // Mock data for demo
      const mockAttachments = [
        {
          id: 1,
          question_id: 1,
          filename: 'project_proposal.pdf',
          file_type: 'pdf',
          file_size: 2048000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user1',
          uploader_name: 'John Doe',
          created_at: '2024-01-15T10:30:00Z',
          question_title: 'How do I upload documents?'
        },
        {
          id: 2,
          question_id: 2,
          filename: 'screenshot.png',
          file_type: 'image',
          file_size: 512000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user2',
          uploader_name: 'Jane Smith',
          created_at: '2024-01-14T14:20:00Z',
          question_title: 'When will the next update be released?'
        },
        {
          id: 3,
          question_id: 3,
          filename: 'video_demo.mp4',
          file_type: 'video',
          file_size: 15728640,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user1',
          uploader_name: 'John Doe',
          created_at: '2024-01-13T09:15:00Z',
          question_title: 'How to invite team members?'
        },
        {
          id: 4,
          question_id: 1,
          filename: 'requirements.docx',
          file_type: 'document',
          file_size: 768000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user3',
          uploader_name: 'Mike Johnson',
          created_at: '2024-01-12T16:45:00Z',
          question_title: 'How do I upload documents?'
        },
        {
          id: 5,
          question_id: 4,
          filename: 'data_analysis.xlsx',
          file_type: 'spreadsheet',
          file_size: 1024000,
          file_url: '#',
          attachment_type: 'file',
          uploaded_by: 'user2',
          uploader_name: 'Jane Smith',
          created_at: '2024-01-11T11:20:00Z',
          question_title: 'Can you help with data analysis?'
        }
      ];

      return { success: true, data: mockAttachments };
    }
  }

  // Delete attachment
  async deleteAttachment(attachmentId) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/attachments/${attachmentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.warn('API not available, using mock delete:', error.message);
      
      // Mock delete for demo
      return { success: true };
    }
  }

  // Download attachment
  async downloadAttachment(attachment) {
    try {
      console.log('Downloading attachment:', attachment);
      
      let fileUrl = attachment.file_url;
      
      // If file_url is missing but we have file_path and storage_bucket, construct it
      if (!fileUrl && attachment.file_path && attachment.storage_bucket) {
        // Get Supabase URL from environment or construct default
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://hrlqnbzcjcmrpjwnoiby.supabase.co';
        fileUrl = `${supabaseUrl}/storage/v1/object/public/${attachment.storage_bucket}/${attachment.file_path}`;
        console.log('Constructed file URL:', fileUrl);
      }
      
      // If attachment has a file_url from Supabase storage, use it directly
      if (fileUrl && fileUrl !== '#') {
        console.log('Using Supabase storage URL:', fileUrl);
        
        // Fetch the file from Supabase storage
        const response = await fetch(fileUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return { success: true };
      }
      
      // Fallback: If it's a mock URL, create a mock download
      if (fileUrl === '#') {
        console.log('Creating mock download');
        const mockContent = `This is a mock ${attachment.file_type} file: ${attachment.filename}`;
        const blob = new Blob([mockContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        return { success: true };
      }

      throw new Error('No valid file URL found');
    } catch (error) {
      console.error('Download failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Get file type from filename
  getFileType(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    
    const typeMap = {
      // Images
      'jpg': 'image', 'jpeg': 'image', 'png': 'image', 'gif': 'image', 'bmp': 'image', 'svg': 'image', 'webp': 'image',
      
      // Documents
      'pdf': 'pdf',
      'doc': 'document', 'docx': 'document',
      'txt': 'text',
      
      // Spreadsheets
      'xls': 'spreadsheet', 'xlsx': 'spreadsheet', 'csv': 'spreadsheet',
      
      // Presentations
      'ppt': 'presentation', 'pptx': 'presentation',
      
      // Videos
      'mp4': 'video', 'avi': 'video', 'mov': 'video', 'wmv': 'video', 'flv': 'video', 'webm': 'video',
      
      // Audio
      'mp3': 'audio', 'wav': 'audio', 'flac': 'audio', 'aac': 'audio', 'ogg': 'audio',
      
      // Archives
      'zip': 'archive', 'rar': 'archive', '7z': 'archive', 'tar': 'archive', 'gz': 'archive',
      
      // Code
      'js': 'code', 'html': 'code', 'css': 'code', 'py': 'code', 'java': 'code', 'cpp': 'code', 'c': 'code'
    };

    return typeMap[extension] || 'file';
  }

  // Validate file
  validateFile(file, maxSize = 10 * 1024 * 1024) { // 10MB default
    const errors = [];

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size exceeds ${this.formatFileSize(maxSize)} limit`);
    }

    // Check file type (basic validation)
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'video/mp4', 'video/avi', 'video/quicktime',
      'audio/mpeg', 'audio/wav', 'audio/ogg',
      'application/zip', 'application/x-rar-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      errors.push('File type not supported');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get file icon class
  getFileIconClass(fileType) {
    const iconMap = {
      'image': 'text-green-600',
      'pdf': 'text-red-600',
      'document': 'text-blue-600',
      'text': 'text-gray-600',
      'spreadsheet': 'text-green-600',
      'presentation': 'text-orange-600',
      'video': 'text-purple-600',
      'audio': 'text-pink-600',
      'archive': 'text-gray-600',
      'code': 'text-yellow-600',
      'file': 'text-gray-600'
    };

    return iconMap[fileType] || 'text-gray-600';
  }

  // Get file type badge color
  getFileTypeBadgeColor(fileType) {
    const colorMap = {
      'image': 'bg-green-100 text-green-700',
      'pdf': 'bg-red-100 text-red-700',
      'document': 'bg-blue-100 text-blue-700',
      'text': 'bg-gray-100 text-gray-700',
      'spreadsheet': 'bg-green-100 text-green-700',
      'presentation': 'bg-orange-100 text-orange-700',
      'video': 'bg-purple-100 text-purple-700',
      'audio': 'bg-pink-100 text-pink-700',
      'archive': 'bg-gray-100 text-gray-700',
      'code': 'bg-yellow-100 text-yellow-700',
      'file': 'bg-gray-100 text-gray-700'
    };

    return colorMap[fileType] || 'bg-gray-100 text-gray-700';
  }
}

const attachmentService = new AttachmentService();
export default attachmentService;
